/**
 * Editing the facts about a policy: the header a surveyor reads.
 *
 * Department, category, type, effective date, review cycle, who must
 * acknowledge it and by when, who owns it. The upload form captured almost
 * none of this, and the record was read-only afterwards.
 *
 * Two regimes, matching every policy system a hospital has used:
 *   - on a draft the owner edits freely;
 *   - on anything published or in review only an administrator may, and it
 *     is an administrative correction — no new version, no re-acknowledgement,
 *     logged — which is exactly what a typo in a title or a departed owner
 *     calls for. The panel says so.
 */

import { useId, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../auth/useAuth';
import { call, isCallFailure } from '../../lib/callable';
import { hasAtLeast, ASSIGNABLE_ROLES, ROLE_DISPLAY_NAMES, type Role } from '../../types/roles';
import { SUMMARY_MAX, TITLE_MAX } from '../../types/fieldLimits';
import { useTaxonomy } from '../admin/useTaxonomy';
import { useMembers } from '../admin/useMembers';
import { CharCount } from '../../shared/CharCount';
import { MultiSelect, type MultiSelectOption } from '../../shared/MultiSelect';
import { REVIEW_CYCLES } from './ReviewAndApproval';
import type { PolicyDetail } from './usePolicy';
import styles from '../../shared/Page.module.css';
import up from './UploadPage.module.css';
import own from './DocumentSection.module.css';
import form from './EditDetailsPanel.module.css';

export interface MetadataInput {
  policyId: string;
  title?: string;
  summary?: string | null;
  categoryId?: string | null;
  documentTypeId?: string | null;
  departmentIds?: string[];
  effectiveDate?: string | null;
  reviewIntervalMonths?: number;
  requiresAcknowledgement?: boolean;
  acknowledgementDueDays?: number;
  targetAudience?: 'all' | 'departments' | 'roles';
  targetDepartmentIds?: string[];
  targetRoles?: string[];
  ownerId?: string;
}

export function useUpdateMetadata(orgId: string | null, policyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MetadataInput) => call<MetadataInput, { changed: boolean; override?: boolean }>('updatePolicyMetadata', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['policy', orgId, policyId] });
      void qc.invalidateQueries({ queryKey: ['policies', orgId] });
      void qc.invalidateQueries({ queryKey: ['policy-counts', orgId] });
    },
  });
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((x) => b.includes(x));
}

/**
 * Only what changed goes to the server, so an administrator correcting a
 * title does not also "change" six untouched fields in the audit trail.
 */
export function diffMetadata(policy: PolicyDetail, next: {
  title: string; summary: string; categoryId: string; documentTypeId: string; departmentIds: string[];
  effectiveDate: string; reviewIntervalMonths: number; requiresAcknowledgement: boolean; acknowledgementDueDays: number;
  targetAudience: 'all' | 'departments' | 'roles'; targetDepartmentIds: string[]; targetRoles: string[]; ownerId: string;
}): Omit<MetadataInput, 'policyId'> {
  const out: Omit<MetadataInput, 'policyId'> = {};
  if (next.title.trim() && next.title.trim() !== policy.title) out.title = next.title.trim();
  if ((next.summary.trim() || null) !== (policy.summary ?? null)) out.summary = next.summary.trim() || null;
  if ((next.categoryId || null) !== (policy.categoryId ?? null)) out.categoryId = next.categoryId || null;
  if ((next.documentTypeId || null) !== (policy.documentTypeId ?? null)) out.documentTypeId = next.documentTypeId || null;
  if (!sameSet(next.departmentIds, policy.departmentIds)) out.departmentIds = next.departmentIds;
  if ((next.effectiveDate || null) !== (policy.effectiveDate ?? null)) out.effectiveDate = next.effectiveDate || null;
  if (next.reviewIntervalMonths !== (policy.reviewCycleMonths ?? 12)) out.reviewIntervalMonths = next.reviewIntervalMonths;
  if (next.requiresAcknowledgement !== policy.requiresAcknowledgement) out.requiresAcknowledgement = next.requiresAcknowledgement;
  if (next.acknowledgementDueDays !== (policy.acknowledgementDueDays ?? 30)) out.acknowledgementDueDays = next.acknowledgementDueDays;
  if (next.targetAudience !== policy.targetAudience) out.targetAudience = next.targetAudience;
  if (!sameSet(next.targetDepartmentIds, policy.targetDepartmentIds)) out.targetDepartmentIds = next.targetDepartmentIds;
  if (!sameSet(next.targetRoles, policy.targetRoles)) out.targetRoles = next.targetRoles;
  if (next.ownerId && next.ownerId !== (policy.ownerId ?? '')) out.ownerId = next.ownerId;
  return out;
}

/**
 * What the Departments control offers: the active departments, any retired
 * one this policy is still filed under, and any id the policy carries that the
 * taxonomy no longer has. That last kind is shown, not hidden: it is saved
 * with the policy either way, and somebody has to be able to see it to remove it.
 */
export function departmentChoices(items: { id: string; name: string; isActive: boolean }[] | undefined, held: readonly string[]): MultiSelectOption[] {
  if (items === undefined) return [];
  const known = items.filter((i) => i.isActive || held.includes(i.id)).map((i) => ({ id: i.id, name: i.name, ...(i.isActive ? {} : { hint: 'No longer in use' }) }));
  const missing = held.filter((h) => !items.some((i) => i.id === h)).map((h) => ({ id: h, name: 'A department that has been deleted', hint: 'Remove it before changing the departments' }));
  return [...known, ...missing];
}

/**
 * Who can be made the owner: the active members, and whoever owns it now
 * even if they have left, so the field shows the truth and not the first
 * name on the list.
 */
export function ownerChoices(members: { uid: string; displayName: string; status: string }[] | undefined, policy: Pick<PolicyDetail, 'ownerId' | 'ownerName'>): { uid: string; name: string }[] {
  const active = (members ?? []).filter((m) => m.status === 'active').map((m) => ({ uid: m.uid, name: m.displayName }));
  if (!policy.ownerId || active.some((m) => m.uid === policy.ownerId)) return active;
  const gone = (members ?? []).find((m) => m.uid === policy.ownerId);
  const name = gone?.displayName ?? policy.ownerName;
  return [{ uid: policy.ownerId, name: name ? `${name} (no longer a member)` : 'Not a member of this organization' }, ...active];
}

export function EditDetailsPanel({ policy, onClose, onSaved }: { policy: PolicyDetail; onClose: () => void; onSaved: (message: string) => void }) {
  const { session } = useAuth();
  const orgId = session?.orgId ?? null;
  const admin = hasAtLeast(session?.roles ?? [], 'admin');
  const override = policy.status !== 'draft';

  const departments = useTaxonomy('department', Boolean(orgId));
  const categories = useTaxonomy('category', Boolean(orgId));
  const types = useTaxonomy('documentType', Boolean(orgId));
  const members = useMembers(admin ? orgId : null);
  const save = useUpdateMetadata(orgId, policy.id);

  const [title, setTitle] = useState(policy.title);
  const [summary, setSummary] = useState(policy.summary ?? '');
  const [categoryId, setCategoryId] = useState(policy.categoryId ?? '');
  const [documentTypeId, setDocumentTypeId] = useState(policy.documentTypeId ?? '');
  const [departmentIds, setDepartmentIds] = useState<string[]>(policy.departmentIds);
  const [effectiveDate, setEffectiveDate] = useState(policy.effectiveDate ?? '');
  const [interval, setInterval] = useState(policy.reviewCycleMonths ?? 12);
  const [requiresAck, setRequiresAck] = useState(policy.requiresAcknowledgement);
  const [dueDays, setDueDays] = useState(policy.acknowledgementDueDays ?? 30);
  const [audience, setAudience] = useState<'all' | 'departments' | 'roles'>(policy.targetAudience);
  const [targetDepartmentIds, setTargetDepartmentIds] = useState<string[]>(policy.targetDepartmentIds);
  const [targetRoles, setTargetRoles] = useState<string[]>(policy.targetRoles);
  const [ownerId, setOwnerId] = useState(policy.ownerId ?? '');
  const ids = useId();
  const id = (n: string) => `${ids}-${n}`;

  const toggle = (list: string[], set: (v: string[]) => void, value: string) =>
    set(list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);

  const cycles: { value: number; label: string }[] = REVIEW_CYCLES.some((c) => c.value === interval)
    ? [...REVIEW_CYCLES]
    : [...REVIEW_CYCLES, { value: interval, label: `Every ${interval} months` }];

  function submit(e: FormEvent) {
    e.preventDefault();
    const changes = diffMetadata(policy, {
      title, summary, categoryId, documentTypeId, departmentIds, effectiveDate, reviewIntervalMonths: interval,
      requiresAcknowledgement: requiresAck, acknowledgementDueDays: dueDays, targetAudience: audience,
      targetDepartmentIds, targetRoles, ownerId,
    });
    if (Object.keys(changes).length === 0) { onSaved('Nothing changed.'); onClose(); return; }
    save.mutate({ policyId: policy.id, ...changes }, {
      onSuccess: (r) => {
        onSaved(r.override ? 'Details corrected. Logged as an administrative correction; no new version.' : 'Details saved.');
        onClose();
      },
    });
  }

  const active = (items: { id: string; name: string; isActive: boolean }[] | undefined, keep: readonly string[]) =>
    (items ?? []).filter((i) => i.isActive || keep.includes(i.id));

  const departmentOptions = departmentChoices(departments.data, policy.departmentIds);
  const targetOptions = departmentChoices(departments.data, policy.targetDepartmentIds);
  // Until the departments have arrived there is nothing to tick, and a
  // policy's departments must not look like "none" in the meantime.
  const departmentsReady = departments.data !== undefined;
  const owners = ownerChoices(members.data, policy);

  return (
    <form className={`${own.panel} ${form.form}`} onSubmit={submit} aria-labelledby={id('t')}>
      <h3 className={own.panelTitle} id={id('t')}>Edit details</h3>
      {override && (
        <p className={`${styles.notice} ${styles.noticeWarn}`} style={{ marginBottom: 'var(--sp-4)' }}>
          This policy is not a draft. Changes here are an administrative correction: no new version is made, nobody is asked to acknowledge again, and the change is logged.
        </p>
      )}
      {save.error && (
        <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">
          {isCallFailure(save.error) ? save.error.message : 'The details could not be saved.'}
        </div>
      )}

      <section className={form.group} aria-labelledby={id('g-info')}>
        <h4 className={form.groupTitle} id={id('g-info')}>Policy information</h4>

        <div className={form.field}>
          <label className={up.label} htmlFor={id('title')}>Title</label>
          <input id={id('title')} className={up.input} maxLength={TITLE_MAX} value={title} onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX))} disabled={save.isPending} />
        </div>

        <div className={form.field}>
          <label className={up.label} htmlFor={id('summary')}>Summary <span className={up.optional}>optional</span></label>
          <textarea id={id('summary')} className={up.textarea} rows={2} maxLength={SUMMARY_MAX} value={summary} onChange={(e) => setSummary(e.target.value.slice(0, SUMMARY_MAX))} disabled={save.isPending} />
          <CharCount value={summary} max={SUMMARY_MAX} />
        </div>

        <div className={`${form.field} ${form.wide}`}>
          <label className={up.label} htmlFor={id('depts')}>Departments</label>
          {departments.isError ? (
            <p className={up.hint} id={id('depts')} role="alert">The departments could not be read, so they cannot be changed here now. Everything else on this form can be saved.</p>
          ) : departmentsReady && departmentOptions.length === 0 ? (
            <p className={up.hint} id={id('depts')}>No departments are set up yet. An administrator adds them under Taxonomy.</p>
          ) : (
            <MultiSelect
              id={id('depts')} label="Departments" options={departmentOptions} value={departmentIds} onChange={setDepartmentIds}
              placeholder={departmentsReady ? 'Select departments' : 'Loading departments…'} disabled={save.isPending || !departmentsReady} searchable chips
            />
          )}
        </div>

        <div className={form.pair}>
          <div className={`${form.field} ${form.medium}`}>
            <label className={up.label} htmlFor={id('cat')}>Category</label>
            <select id={id('cat')} className={up.select} value={categoryId} onChange={(e) => setCategoryId(e.target.value)} disabled={save.isPending}>
              <option value="">None</option>
              {active(categories.data, [categoryId]).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className={`${form.field} ${form.medium}`}>
            <label className={up.label} htmlFor={id('type')}>Document type</label>
            <select id={id('type')} className={up.select} value={documentTypeId} onChange={(e) => setDocumentTypeId(e.target.value)} disabled={save.isPending}>
              <option value="">None</option>
              {active(types.data, [documentTypeId]).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>
      </section>

      <section className={form.group} aria-labelledby={id('g-review')}>
        <h4 className={form.groupTitle} id={id('g-review')}>Review</h4>

        <div className={form.pair}>
          <div className={`${form.field} ${form.short}`}>
            <label className={up.label} htmlFor={id('eff')}>Effective date</label>
            <input id={id('eff')} type="date" className={up.input} value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} disabled={save.isPending} aria-describedby={id('eff-hint')} />
          </div>
          <div className={`${form.field} ${form.medium}`}>
            <label className={up.label} htmlFor={id('cycle')}>Review cycle</label>
            <select id={id('cycle')} className={up.select} value={interval} onChange={(e) => setInterval(Number(e.target.value))} disabled={save.isPending}>
              {cycles.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
        </div>
        <p className={`${up.hint} ${form.pairHint}`} id={id('eff-hint')}>{override ? 'Changing the effective date recomputes the next review date from the review cycle.' : 'The effective date is set when the policy is published if left empty.'}</p>

        {admin && (
          <div className={`${form.field} ${form.wide}`}>
            <label className={up.label} htmlFor={id('owner')}>Owner</label>
            <select id={id('owner')} className={up.select} value={ownerId} onChange={(e) => setOwnerId(e.target.value)} disabled={save.isPending || members.data === undefined} aria-describedby={id('owner-hint')}>
              {!ownerId && <option value="">Not set</option>}
              {owners.map((m) => <option key={m.uid} value={m.uid}>{m.name}</option>)}
            </select>
            <p className={up.hint} id={id('owner-hint')}>The one person accountable for keeping it current. They are emailed when it changes hands.</p>
          </div>
        )}
      </section>

      <section className={form.group} aria-labelledby={id('g-ack')}>
        <h4 className={form.groupTitle} id={id('g-ack')}>Acknowledgement</h4>
        <label className={up.check}>
          <input type="checkbox" checked={requiresAck} onChange={(e) => setRequiresAck(e.target.checked)} disabled={save.isPending} />
          <span>Staff must acknowledge this policy</span>
        </label>
        {/* Shown only when it applies. What was chosen stays in the form while hidden, and nothing hidden is sent unless it was changed. */}
        {requiresAck && (
          <div className={form.nested}>
            <div className={`${form.field} ${form.tiny}`}>
              <label className={up.label} htmlFor={id('due')}>Days to acknowledge</label>
              <input id={id('due')} type="number" min={1} max={365} className={up.input} value={dueDays} aria-describedby={id('due-hint')}
                onChange={(e) => setDueDays(Math.min(365, Math.max(1, Number(e.target.value) || 1)))} disabled={save.isPending} />
            </div>
            <p className={`${up.hint} ${form.pairHint}`} id={id('due-hint')}>From the effective date. Counted for each person it is assigned to.</p>

            <div className={`${form.field} ${form.wide}`}>
              <label className={up.label} htmlFor={id('aud')}>Who must acknowledge it</label>
              <select id={id('aud')} className={up.select} value={audience} onChange={(e) => setAudience(e.target.value as 'all' | 'departments' | 'roles')} disabled={save.isPending} aria-describedby={id('aud-hint')}>
                <option value="all">Everyone in the organization</option>
                <option value="departments">People in certain departments</option>
                <option value="roles">People with certain roles</option>
              </select>
            </div>
            {audience === 'departments' && (
              <div className={`${form.field} ${form.wide}`}>
                <label className={up.label} htmlFor={id('aud-depts')}>Departments that must acknowledge it</label>
                <MultiSelect
                  id={id('aud-depts')} label="Departments that must acknowledge it" options={targetOptions} value={targetDepartmentIds} onChange={setTargetDepartmentIds}
                  placeholder={departmentsReady ? 'Select departments' : 'Loading departments…'} disabled={save.isPending || !departmentsReady} searchable chips
                />
              </div>
            )}
            {audience === 'roles' && (
              <fieldset className={form.roles}>
                <legend className={up.label}>Roles that must acknowledge it</legend>
                {ASSIGNABLE_ROLES.map((r: Role) => (
                  <label key={r} className={up.check}>
                    <input type="checkbox" checked={targetRoles.includes(r)} onChange={() => toggle(targetRoles, setTargetRoles, r)} disabled={save.isPending} />
                    <span>{ROLE_DISPLAY_NAMES[r]}</span>
                  </label>
                ))}
              </fieldset>
            )}
            <p className={up.hint} id={id('aud-hint')}>Applies when the next version is published. People already asked keep their assignment.</p>
          </div>
        )}
      </section>

      <div className={styles.actions}>
        <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={save.isPending || !title.trim()}>{save.isPending ? 'Saving…' : 'Save details'}</button>
        <button type="button" className={styles.btn} onClick={onClose} disabled={save.isPending}>Cancel</button>
      </div>
    </form>
  );
}
